import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { tenants } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_authed/onboarding/language")({
  component: LanguagePickerPage,
});
// navigate to: "/_authed/onboarding/first-meeting"
// navigate back to: "/_authed/onboarding/channels"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Language = "id" | "en" | "bilingual";

const LANGUAGES: { value: Language; label: string; sublabel: string; flag: string }[] = [
  { value: "id", label: "Indonesian", sublabel: "Bahasa Indonesia", flag: "🇮🇩" },
  { value: "en", label: "English", sublabel: "English only", flag: "🇬🇧" },
  { value: "bilingual", label: "Bilingual", sublabel: "ID + EN side-by-side", flag: "🌐" },
];

// ---------------------------------------------------------------------------
// Bilingual preview
// ---------------------------------------------------------------------------

function BilingualPreview() {
  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-[color:var(--color-abyssal)]/10 bg-[color:var(--color-abyssal)]/[0.02]">
      <div className="border-b border-[color:var(--color-abyssal)]/10 px-4 py-2.5 flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-[color:var(--color-north-star)]/40" />
        <span className="text-xs text-[color:var(--color-abyssal)]/40 font-medium">Preview</span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-[color:var(--color-abyssal)]/10">
        {/* Indonesian side */}
        <div className="p-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-abyssal)]/40">
            🇮🇩 Ringkasan
          </p>
          <p className="text-xs text-[color:var(--color-abyssal)]/70 leading-relaxed">
            Tim menyetujui peluncuran fitur baru pada Q3. Rina akan memimpin implementasi.
          </p>
          <div className="pt-1">
            <p className="text-xs font-medium text-[color:var(--color-abyssal)]/50 mb-1">Tindak lanjut</p>
            <p className="text-xs text-[color:var(--color-abyssal)]/60">• Rina — Buat jadwal sprint</p>
          </div>
        </div>
        {/* English side */}
        <div className="p-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-abyssal)]/40">
            🇬🇧 Summary
          </p>
          <p className="text-xs text-[color:var(--color-abyssal)]/70 leading-relaxed">
            Team agreed to launch the new feature in Q3. Rina will lead the implementation.
          </p>
          <div className="pt-1">
            <p className="text-xs font-medium text-[color:var(--color-abyssal)]/50 mb-1">Action items</p>
            <p className="text-xs text-[color:var(--color-abyssal)]/60">• Rina — Create sprint schedule</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function LanguagePickerPage() {
  const navigate = useNavigate();
  const tenantsList = useAuthStore((s) => s.tenants);
  const tenant = tenantsList[0] ?? null;

  const [selected, setSelected] = useState<Language>("bilingual");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-select whatever the operator set when creating the invite, or default
  // to bilingual per the acceptance criteria.
  useEffect(() => {
    if (!tenant) return;
    tenants
      .getSettings(tenant.id)
      .then((s) => setSelected(s.summary_language))
      .catch(() => {
        // Non-fatal — default stays bilingual.
      });
  }, [tenant?.id]);

  const handleNext = async () => {
    if (!tenant) {
      void navigate({ to: "/onboarding/first-meeting" });
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await tenants.updateSettings(tenant.id, { summary_language: selected });
      await tenants.updateOnboardingStep(tenant.id, 4).catch(() => {});
      await useAuthStore.getState().refresh();
      void navigate({ to: "/onboarding/first-meeting" });
    } catch {
      setError("Couldn't save your preference. Please try again.");
      setSaving(false);
    }
  };

  return (
    <PageShell>
      <StepIndicator current={3} total={4} />

      <div className="mt-6">
        <h1 className="font-semibold tracking-tight text-3xl text-[color:var(--color-abyssal)]">
          Pick your summary language.
        </h1>
        <p className="mt-2 text-[color:var(--color-abyssal)]/60">
          All meeting summaries for this workspace will use this language. You can change it later in settings.
        </p>
      </div>

      <div className="mt-7 space-y-3">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.value}
            type="button"
            onClick={() => setSelected(lang.value)}
            className={`w-full rounded-2xl border-2 p-4 text-left transition-all ${
              selected === lang.value
                ? "border-[color:var(--color-north-star)] bg-[color:var(--color-north-star)]/[0.03]"
                : "border-[color:var(--color-abyssal)]/10 bg-white hover:border-[color:var(--color-abyssal)]/20"
            }`}
          >
            <div className="flex items-center gap-3">
              {/* Radio indicator */}
              <div className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                selected === lang.value
                  ? "border-[color:var(--color-north-star)]"
                  : "border-[color:var(--color-abyssal)]/20"
              }`}>
                {selected === lang.value && (
                  <div className="h-2 w-2 rounded-full bg-[color:var(--color-north-star)]" />
                )}
              </div>

              {/* Flag + label */}
              <span className="text-xl">{lang.flag}</span>
              <div>
                <p className="font-medium text-sm text-[color:var(--color-abyssal)]">{lang.label}</p>
                <p className="text-xs text-[color:var(--color-abyssal)]/50">{lang.sublabel}</p>
              </div>

              {/* Selected checkmark */}
              {selected === lang.value && (
                <div className="ml-auto">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="8" fill="var(--color-north-star)" opacity="0.15" />
                    <path d="M5 8l2 2 4-4" stroke="var(--color-north-star)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Bilingual preview — only shown when bilingual is selected */}
      {selected === "bilingual" && <BilingualPreview />}

      {error ? (
        <p className="mt-4 text-sm text-[color:var(--color-coral)]">{error}</p>
      ) : null}

      {/* Actions */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={() => void navigate({ to: "/onboarding/channels" })}
          className="text-sm text-[color:var(--color-abyssal)]/50 hover:text-[color:var(--color-abyssal)]/80 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          disabled={saving}
          className="rounded-md bg-[color:var(--color-north-star)] px-5 py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-40"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <Spinner small />
              Saving…
            </span>
          ) : (
            "Next →"
          )}
        </button>
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16">
      <div className="mb-8 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[color:var(--color-north-star)]">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 4h8M3 7h5M3 10h6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <span className="font-semibold text-[color:var(--color-abyssal)]">Jotty</span>
      </div>
      {children}
    </main>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${
              i + 1 === current
                ? "w-6 bg-[color:var(--color-north-star)]"
                : i + 1 < current
                  ? "w-4 bg-[color:var(--color-north-star)]/40"
                  : "w-4 bg-[color:var(--color-abyssal)]/10"
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-[color:var(--color-abyssal)]/50">
        Step {current} of {total}
      </span>
    </div>
  );
}

function Spinner({ small }: { small?: boolean }) {
  const size = small ? 14 : 20;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin" aria-hidden>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
    </svg>
  );
}